import React, { FormEvent, useState } from 'react';

import apiClient from '../services/api';
import useResource from '../services/useResource';
import { HashtagPreset, MaybePaginated } from '../types/api';
import './HashtagsPage.css';

export default function HashtagsPage() {
  const [actionError, setActionError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { data: presets, loading, error, reload } = useResource<HashtagPreset[], MaybePaginated<HashtagPreset>>(
    () => apiClient.get<MaybePaginated<HashtagPreset>>('/posts/hashtag-presets/'),
    [],
    { initial: [], transform: (d) => (Array.isArray(d) ? d : d.results || []) },
  );

  function startEdit(preset: HashtagPreset): void {
    setEditingId(preset.id);
    setName(preset.name);
    setHashtags(preset.hashtags);
    setActionError(null);
  }

  function startNew(): void {
    setEditingId(null);
    setName('');
    setHashtags('');
    setActionError(null);
  }

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!name.trim() || !hashtags.trim()) {
      setActionError('Both name and hashtags are required.');
      return;
    }
    setActionError(null);
    setSubmitting(true);
    try {
      if (editingId !== null) {
        await apiClient.put(`/posts/hashtag-presets/${editingId}/`,
                            { name: name.trim(), hashtags: hashtags.trim() });
      } else {
        await apiClient.post('/posts/hashtag-presets/',
                              { name: name.trim(), hashtags: hashtags.trim() });
      }
      startNew();
      reload();
    } catch (err) {
      setActionError((err as Error).message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function remove(id: number): Promise<void> {
    if (!window.confirm('Delete this preset?')) return;
    try {
      await apiClient.delete(`/posts/hashtag-presets/${id}/`);
      if (editingId === id) startNew();
      reload();
    } catch (err) {
      setActionError((err as Error).message || 'Delete failed');
    }
  }

  const errorMessage = actionError || error?.message;

  return (
    <div className="hashtags-page">
      <h2>Hashtag Presets</h2>
      <p className="muted hashtags-intro">
        Save reusable batches of hashtags and append them to any post with one click.
      </p>
      {errorMessage && <div className="posts-error">{errorMessage}</div>}

      <div className="hashtags-layout">
        <form className="post-form hashtag-form" onSubmit={handleSubmit}>
          <h3 className="hashtag-form-title">{editingId !== null ? 'Edit preset' : 'New preset'}</h3>
          <label>
            Name
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Brand defaults"
              required
            />
          </label>
          <label>
            Hashtags
            <textarea
              rows={4}
              value={hashtags}
              onChange={(e) => setHashtags(e.target.value)}
              placeholder="#yourbrand #campaign #launch"
              required
            />
          </label>
          <div className="hashtag-form-actions">
            <button type="submit" className="primary" disabled={submitting}>
              {submitting ? 'Saving…' : (editingId !== null ? 'Update preset' : 'Save preset')}
            </button>
            {editingId !== null && (
              <button type="button" className="ghost" onClick={startNew}>
                Cancel
              </button>
            )}
          </div>
        </form>

        <section className="hashtag-list">
          <h3>Your presets ({presets.length})</h3>
          {loading && <p className="muted">Loading…</p>}
          {!loading && presets.length === 0 && (
            <p className="muted">No presets yet — create one on the left.</p>
          )}
          <ul>
            {presets.map((p) => (
              <li key={p.id} className={`hashtag-preset ${editingId === p.id ? 'is-editing' : ''}`}>
                <div className="hashtag-preset-head">
                  <span className="hashtag-preset-name">{p.name}</span>
                  <div className="hashtag-preset-actions">
                    <button className="ghost btn--sm" onClick={() => startEdit(p)}>Edit</button>
                    <button className="danger" onClick={() => remove(p.id)}>Delete</button>
                  </div>
                </div>
                <div className="hashtag-preset-body">{p.hashtags}</div>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
